import React from 'react';
import { ItemModifier, ModifierFormulaObject } from '../../../types/equipment';
import { CharacterData } from '../../../types/character';
import styles from './ModifierBreakdown.module.css';

interface ModifierBreakdownProps {
  modifiers: ItemModifier[];
  character?: CharacterData;
  tier?: number;
}

/**
 * Helper function to calculate modifier value
 */
const calculateModifierValue = (
  value: number | ModifierFormulaObject,
  character?: CharacterData,
  tier?: number
): number | string => {
  // If it's a plain number, return it
  if (typeof value === 'number') {
    return value;
  }

  // Handle formula objects
  const formula = value as ModifierFormulaObject;

  switch (formula.type) {
    case 'character_tier':
      if (tier !== undefined) {
        return tier * (formula.multiplier || 1);
      }
      return '{tier}';

    case 'character_level':
      if (character?.level) {
        const level = parseInt(character.level, 10);
        return level * (formula.multiplier || 1);
      }
      return '{level}';

    case 'trait_direct':
      if (character && formula.trait) {
        return character.traits[formula.trait] || 0;
      }
      return `{${formula.trait}}`;

    case 'trait_half':
      if (character && formula.trait) {
        return Math.floor((character.traits[formula.trait] || 0) / 2);
      }
      return `{${formula.trait}/2}`;

    case 'trait_multiply':
      if (character && formula.trait && formula.multiplier) {
        return (character.traits[formula.trait] || 0) * formula.multiplier;
      }
      return `{${formula.trait}×${formula.multiplier}}`;

    case 'expression':
      return formula.formula || '{formula}';

    default:
      return '{formula}';
  }
};

/**
 * Format modifier operation for display
 */
const formatOperation = (operation: string): string => {
  switch (operation) {
    case 'add':
      return '+';
    case 'multiply':
      return '×';
    case 'set':
      return '=';
    case 'replace':
      return '→';
    default:
      return operation;
  }
};

/**
 * Group modifiers by target stat
 */
const groupModifiersByTarget = (modifiers: ItemModifier[]): Map<string, ItemModifier[]> => {
  const grouped = new Map<string, ItemModifier[]>();

  modifiers.forEach(modifier => {
    const existing = grouped.get(modifier.target) || [];
    grouped.set(modifier.target, [...existing, modifier]);
  });

  return grouped;
};

/**
 * Format target stat name for display
 */
const formatTargetName = (target: string): string => {
  // Handle nested paths like 'traits.agility'
  const parts = target.split('.');
  const lastPart = parts[parts.length - 1];

  // Capitalize first letter
  return lastPart.charAt(0).toUpperCase() + lastPart.slice(1);
};

/**
 * ModifierBreakdown - Display stat modifiers from items in readable format
 * Shows modifier operations with scaling info and source context
 */
export const ModifierBreakdown: React.FC<ModifierBreakdownProps> = ({
  modifiers,
  character,
  tier
}) => {
  if (!modifiers || modifiers.length === 0) {
    return null;
  }

  const groupedModifiers = groupModifiersByTarget(modifiers);

  return (
    <div className={styles.modifierBreakdown}>
      <h4 className={styles.title}>Stat Modifiers</h4>
      <ul className={styles.modifierList}>
        {Array.from(groupedModifiers.entries()).map(([target, mods]) => (
          <li key={target} className={styles.modifierItem}>
            <span className={styles.statName}>{formatTargetName(target)}:</span>
            {mods.map((modifier, idx) => {
              const calculatedValue = calculateModifierValue(
                modifier.value,
                character,
                tier
              );
              const isFormula = typeof calculatedValue === 'string';

              return (
                <span key={idx} className={styles.modifierValue}>
                  <span className={styles.operation}>
                    {formatOperation(modifier.operation)}
                  </span>
                  <span className={styles.value}>
                    {calculatedValue}
                  </span>
                  {isFormula && (
                    <span className={styles.scaling} title="This value scales">
                      (scales with {typeof modifier.value !== 'number' ?
                        (modifier.value as ModifierFormulaObject).type.replace('_', ' ') :
                        'character'})
                    </span>
                  )}
                  {modifier.conditions && modifier.conditions.length > 0 && (
                    <span className={styles.conditional} title="Has conditions">
                      (conditional)
                    </span>
                  )}
                </span>
              );
            })}
            <span className={styles.source}>
              (from {mods[0].source.name})
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};
